import { useState, useEffect, useCallback } from 'react';
import usePerimetre from '../hooks/usePerimetre';
import axios from 'axios';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import NotificationBell from '../components/NotificationBell';

const API = process.env.REACT_APP_API_URL || '';

const MOIS_NOMS = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'];

const COLS = [
  { key: 'h_normales',    label: 'H.Norm',  color: '#1B5E20' },
  { key: 'h_hs_15',       label: 'HS 15%',  color: '#F57F17' },
  { key: 'h_hs_40',       label: 'HS 40%',  color: '#BF360C' },
  { key: 'h_dimanche',    label: 'Dim',     color: '#1565C0' },
  { key: 'h_ferie_jour',  label: 'Férié',   color: '#6A1B9A' },
  { key: 'h_nuit_total',  label: 'Nuit',    color: '#0D47A1' },
  { key: 'nb_paniers',    label: 'Paniers', color: '#558B2F' },
  { key: 'h_absent_just', label: 'Abs.J',   color: '#FF8F00' },
  { key: 'h_absent_injust','label':'Abs.IJ', color: '#C62828' },
  { key: 'h_conge',       label: 'Congé',   color: '#00695C' },
];

const CHART_KEYS = [
  { key: 'h_normales', color: '#1B5E20', label: 'Normal' },
  { key: 'h_hs_15',    color: '#F57F17', label: 'HS 15%' },
  { key: 'h_hs_40',    color: '#BF360C', label: 'HS 40%' },
  { key: 'h_nuit_total', color: '#0D47A1', label: 'Nuit' },
];

function fmt2(val) {
  const n = parseFloat(val || 0);
  return n === 0 ? '—' : n.toFixed(2);
}

function Skeleton({ h = 14 }) {
  return (
    <div style={{
      height: h,
      background: 'linear-gradient(90deg,#e0e0e0 25%,#f5f5f5 50%,#e0e0e0 75%)',
      backgroundSize: '200% 100%', animation: 'shimmer 1.5s infinite', borderRadius: 4,
    }} />
  );
}

// ─── Graphique barres simple (pur CSS/SVG) ────────────────────────────────────
function BarChart({ data }) {
  if (!data || data.length === 0) return null;

  // Construire les données par mois (1→12)
  const parMois = Array.from({ length: 12 }, (_, i) => {
    const row = data.find(d => parseInt(d.mois) === i + 1) || {};
    return { mois: MOIS_NOMS[i], ...row };
  });

  const maxVal = Math.max(...parMois.flatMap(m =>
    CHART_KEYS.map(k => parseFloat(m[k.key] || 0))
  ), 1);

  const H = 160;
  const barW = 14;
  const gap = 4;
  const groupW = CHART_KEYS.length * (barW + gap) + 8;
  const totalW = parMois.length * groupW + 40;

  return (
    <div style={{ overflowX: 'auto', padding: '0 0 8px' }}>
      {/* Légende */}
      <div style={{ display: 'flex', gap: 16, marginBottom: 10, flexWrap: 'wrap' }}>
        {CHART_KEYS.map(k => (
          <div key={k.key} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
            <div style={{ width: 12, height: 12, background: k.color, borderRadius: 2 }} />
            <span>{k.label}</span>
          </div>
        ))}
      </div>
      <svg width={totalW} height={H + 30} style={{ display: 'block' }}>
        {parMois.map((m, mi) => (
          <g key={mi} transform={`translate(${40 + mi * groupW}, 0)`}>
            {CHART_KEYS.map((k, ki) => {
              const val = parseFloat(m[k.key] || 0);
              const bh = Math.max((val / maxVal) * H, val > 0 ? 2 : 0);
              return (
                <g key={k.key}>
                  <rect
                    x={ki * (barW + gap)}
                    y={H - bh}
                    width={barW}
                    height={bh}
                    fill={k.color}
                    rx={2}
                    opacity={val > 0 ? 0.85 : 0}
                  />
                  {val > 0 && bh > 18 && (
                    <text
                      x={ki * (barW + gap) + barW / 2}
                      y={H - bh + 10}
                      textAnchor="middle"
                      fontSize={8}
                      fill="#fff"
                      fontWeight="700"
                    >
                      {val.toFixed(0)}
                    </text>
                  )}
                </g>
              );
            })}
            <text x={(groupW - 8) / 2} y={H + 16} textAnchor="middle" fontSize={11} fill="#555">
              {m.mois}
            </text>
          </g>
        ))}
        {/* Ligne de base */}
        <line x1={38} y1={H} x2={totalW} y2={H} stroke="#ddd" strokeWidth={1} />
      </svg>
    </div>
  );
}

// ─── Page principale ──────────────────────────────────────────────────────────
export default function RecapAnnuelPage() {
  const { employe, token } = useAuth();
  const today = new Date();
  const [annee, setAnnee] = useState(today.getFullYear());
  const [siteId, setSiteId] = useState('');
  // Module 0 (clôture) : sites du périmètre réel uniquement.
  const { perimetre, sites } = usePerimetre('peut_voir_temps_site');
  useEffect(() => {
    if (perimetre && !perimetre.global && sites.length === 1) setSiteId(sites[0].id);
  }, [perimetre, sites]);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);


  const charger = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ annee });
      if (siteId) params.append('site_id', siteId);
      const { data: d } = await axios.get(`${API}/api/temps/recap/annuel?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(d.data || []);
    } catch { setData([]); } finally { setLoading(false); }
  }, [annee, siteId, token]);

  useEffect(() => { charger(); }, [charger]);

  const handleExport = () => {
    const blob = new Blob([JSON.stringify({ annee, data }, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `recap_annuel_${annee}.json`;
    a.click();
    setToast({ msg: 'Export téléchargé', type: 'success' });
  };

  // Tableau complet 12 mois
  const lignes = Array.from({ length: 12 }, (_, i) => {
    const row = data.find(d => parseInt(d.mois) === i + 1) || {};
    return { mois: i + 1, nom: MOIS_NOMS[i], ...row };
  });

  const totaux = data.reduce((acc, d) => {
    COLS.forEach(c => { acc[c.key] = (acc[c.key] || 0) + parseFloat(d[c.key] || 0); });
    return acc;
  }, {});

  const annees = Array.from({ length: 5 }, (_, i) => today.getFullYear() - 2 + i);

  return (
    <Layout>
      <style>{`@keyframes shimmer{0%{background-position:200% 0}100%{background-position:-200% 0}} .t-scroll{overflow-x:auto;}`}</style>

      {/* Header */}
      <div className="sg-bandeau-clair" style={{
        background: '#1B5E20', color: '#fff', borderRadius: '14px 14px 0 0',
        padding: '14px 20px', display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center',
      }}>
        <h2 style={{ margin: 0, fontSize: 20, flex: 1 }}>Récapitulatif Annuel</h2>
        <NotificationBell couleurIcone="#fff" taille={17} />
        <select value={annee} onChange={e => setAnnee(+e.target.value)}
          style={{ padding: '6px 12px', borderRadius: 8, border: 'none', fontSize: 13 }}>
          {annees.map(a => <option key={a} value={a}>{a}</option>)}
        </select>
        {sites.length > 1 && (
          <select value={siteId} onChange={e => setSiteId(e.target.value)}
            style={{ padding: '6px 12px', borderRadius: 8, border: 'none', fontSize: 13 }}>
            <option value="">{perimetre?.global ? 'Tous les sites' : 'Tous mes sites'}</option>
            {sites.map(s => <option key={s.id} value={s.id}>{s.nom}</option>)}
          </select>
        )}
        <button onClick={handleExport} style={{ background: 'rgba(255,255,255,0.2)', color: '#fff', border: '1px solid rgba(255,255,255,0.4)', borderRadius: 8, padding: '8px 16px', cursor: 'pointer', fontSize: 13, fontWeight: 700 }}>
          Exporter l'année
        </button>
      </div>

      <div style={{ background: '#fff', borderRadius: '0 0 14px 14px', boxShadow: '0 2px 12px rgba(0,0,0,0.08)', overflow: 'hidden' }}>

        {/* ─── Tableau annuel ─── */}
        <div style={{ padding: '20px 20px 8px' }}>
          <div className="t-scroll">
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 800 }}>
              <thead>
                <tr style={{ background: '#1B5E20', color: '#fff' }}>
                  <th style={thS}>Mois</th>
                  {COLS.map(c => <th key={c.key} style={thS}>{c.label}</th>)}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  Array(12).fill(0).map((_, i) => (
                    <tr key={i}>{Array(COLS.length + 1).fill(0).map((_, j) => (
                      <td key={j} style={{ padding: 10 }}><Skeleton /></td>
                    ))}</tr>
                  ))
                ) : (
                  lignes.map(l => {
                    const hasData = COLS.some(c => parseFloat(l[c.key] || 0) > 0);
                    return (
                      <tr key={l.mois} style={{
                        borderBottom: '1px solid #f0f0f0',
                        background: hasData ? '#fff' : '#fafafa',
                        opacity: hasData ? 1 : 0.6,
                      }}>
                        <td style={{ ...tdS, fontWeight: 700, color: '#1B5E20' }}>{l.nom}</td>
                        {COLS.map(c => (
                          <td key={c.key} style={{ ...tdS, textAlign: 'center', color: parseFloat(l[c.key] || 0) > 0 ? c.color : '#ccc', fontWeight: parseFloat(l[c.key] || 0) > 0 ? 700 : 400 }}>
                            {fmt2(l[c.key])}
                          </td>
                        ))}
                      </tr>
                    );
                  })
                )}
              </tbody>
              {!loading && (
                <tfoot>
                  <tr style={{ background: '#E8F5E9', fontWeight: 700, borderTop: '2px solid #1B5E20' }}>
                    <td style={{ ...tdS, color: '#1B5E20', fontWeight: 800 }}>TOTAL {annee}</td>
                    {COLS.map(c => (
                      <td key={c.key} style={{ ...tdS, textAlign: 'center', color: '#1B5E20', fontWeight: 800 }}>
                        {fmt2(totaux[c.key])}
                      </td>
                    ))}
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>

        {/* ─── Graphique ─── */}
        <div style={{ padding: '16px 20px 24px', borderTop: '1px solid #f0f0f0' }}>
          <h3 style={{ color: '#1B5E20', margin: '0 0 14px', fontSize: 15 }}>Évolution mensuelle {annee}</h3>
          {loading ? <Skeleton h={180} /> : <BarChart data={data} />}
        </div>
      </div>

      {toast && (
        <div style={{
          position: 'fixed', bottom: 24, right: 24, background: '#1B5E20', color: '#fff',
          padding: '12px 20px', borderRadius: 10, zIndex: 9999, fontSize: 14,
          boxShadow: '0 4px 16px rgba(0,0,0,0.25)',
        }}
          onClick={() => setToast(null)}
        >{toast.msg}</div>
      )}
    </Layout>
  );
}

const thS = { padding: '10px 12px', textAlign: 'left', fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap' };
const tdS = { padding: '10px 12px', fontSize: 13 };
